import { FC, useState } from "react";
import { Box, Typography, Card, CardContent, Chip, IconButton, Popover } from "@material-ui/core";
import { AppThemeIconProperty } from "../Types";
import { Edit } from "@material-ui/icons";
import IconPopover from "../Popovers/IconPopover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIcons } from "src/contexts/IconContext";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

type IconPropertyCardProps = {
  property: AppThemeIconProperty;
};

const IconPropertyCard: FC<IconPropertyCardProps> = ({ property }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { icons } = useIcons();

  const getIconDefinition = (
    iconValue: string,
    type: string | undefined
  ): IconDefinition | null => {
    
    if (!iconValue || !icons.length) {
      return null;
    }

    const getStylePrefix = (style: string): string => {
      const styleMap: Record<string, string> = {
        'solid': 'fas',
        'regular': 'far',
        'light': 'fal',
        'thin': 'fat',
        'duotone': 'fad'
      };
      return styleMap[style] || 'fas';
    };

    let matchedIcon = null;

    if (iconValue.startsWith("0x")) {
      const hex = iconValue.toLowerCase().replace(/^0x/, "");
      
      matchedIcon = icons.find(icon => {
        const matches = icon.unicode.toLowerCase() === hex &&
          (!type || icon.type === type || icon.style === type);

        return matches;
      });
    } else if (iconValue.includes(':')) {
      const [styleOrPrefix, iconName] = iconValue.split(':');
      
      matchedIcon = icons.find(icon => 
        icon.iconName === iconName && 
        (icon.style === styleOrPrefix || getStylePrefix(icon.style) === styleOrPrefix)
      );
    } else {
      matchedIcon = icons.find(icon => 
        icon.iconName === iconValue &&
        (!type || icon.type === type || icon.style === type)
      );
    }

    if (!matchedIcon) {
      return null;
    }

    const iconDefinition: IconDefinition = {
      prefix: getStylePrefix(matchedIcon.style) as any,
      iconName: matchedIcon.iconName as any,
      icon: [
        matchedIcon.viewBox[2], // width
        matchedIcon.viewBox[3], // height
        [], // ligatures
        matchedIcon.unicode, // unicode
        Array.isArray(matchedIcon.svgPath) ? matchedIcon.svgPath : [matchedIcon.svgPath] // path(s)
      ]
    };

    return iconDefinition;
  };
    
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const open = Boolean(anchorEl);

  const iconDefinition = getIconDefinition(property.icon, property.type);

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          transition: "all 0.2s ease-in-out",
          "&:hover": { boxShadow: 1, transform: "translateY(-1px)" },
        }}
      >
        <CardContent
          sx={{
            py: 1.5,
            px: 2,
            "&:last-child": { pb: 1.5 },
            "&:first-child": { pt: 1.5 },
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Box height="100%">
              <Typography variant="subtitle2" fontWeight={600} color="primary">
                {property.label}
              </Typography>
              <Box display="flex" flexDirection="column">
                {/* Row 1: Icon, Color, Opacity */}
                <Box display="flex" flexDirection="row" flexWrap="wrap">
                  {property.colour !== undefined && (
                    <Chip
                      label={`Color: ${property.colour}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.75rem", mt: 0.5, mr: 0.5 }}
                    />
                  )}
                  {property.alpha !== undefined && (
                    <Chip
                      label={`Opacity: ${property.alpha ?? 1}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.75rem", mt: 0.5 }}
                    />
                  )}
                </Box>
                
                {/* Row 2: Type, Size */}
                <Box display="flex" flexDirection="row" flexWrap="wrap" mt={0.5}>
                  {property.icon !== undefined && (
                    <Chip
                      label={`Icon: ${property.icon}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.75rem", mt: 0.5, mr: 0.5 }}
                    />
                  )}
                  {property.type !== undefined && (
                    <Chip
                      label={`Type: ${property.type}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.75rem", mt: 0.5, mr: 0.5 }}
                    />
                  )}
                  {property.size !== undefined && (
                    <Chip
                      label={`Size: ${property.size}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.75rem", mt: 0.5 }}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            <Box display="flex" alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {iconDefinition ? (
                  <FontAwesomeIcon
                    icon={iconDefinition}
                    style={{
                      fontSize: '20px',
                      color: property.colour,
                      opacity: property.alpha ?? 1,
                      ['--fa-secondary-opacity' as any]: 1,
                      ['--fa-primary-opacity' as any]: 1,
                    }}
                  />
                ) : (
                  <Typography variant="caption" color="textSecondary" fontSize="0.6rem">
                    No Icon
                  </Typography>
                )}
              </Box>
              <IconButton size="small" onClick={handleOpen}>
                <Edit fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={{ top: window.innerHeight / 2, left: window.innerWidth / 2 }}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            p: 1,
            width: "min-content",
          },
        }}
      >
        <IconPopover property={property} />
      </Popover>
    </>
  );
};

export default IconPropertyCard;